'use client'

import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FiPlus, FiTrash2, FiServer, FiUsers, FiSearch, FiCode } from 'react-icons/fi'
import Swal from 'sweetalert2'
import HostingOverviewPage from './components/HostingOverview'

export default function HostingPage() {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null)
  const [activeTab, setActiveTab] = useState<'accounts' | 'users'>('users')
  const [accounts, setAccounts] = useState<any[]>([])
  const [hostingUsers, setHostingUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkRoleAndFetch()
  }, [])

  const checkRoleAndFetch = async () => {
    try {
      const res = await fetch('/api/auth/verificar')
      const data = await res.json()
      if (data.success) {
        setIsAdmin(data.usuario.isAdmin)
        if (data.usuario.isAdmin) {
          fetchData()
        }
      }
    } catch (error) {
      console.error('Error verifying role:', error)
    }
  }

  const fetchData = async () => {
    setLoading(true)
    await Promise.all([fetchAccounts(), fetchHostingUsers()])
    setLoading(false)
  }

  const fetchAccounts = async () => {
    try {
      const res = await fetch('/api/hosting/accounts')
      const data = await res.json()
      if (Array.isArray(data)) setAccounts(data)
    } catch (e) {
      setAccounts([])
    }
  }

  const fetchHostingUsers = async () => {
    try {
      const res = await fetch('/api/hosting/users')
      const data = await res.json()
      if (Array.isArray(data)) setHostingUsers(data)
    } catch (e) {
      setHostingUsers([])
    }
  }

  const handleCreateUser = async () => {
    const { value: formValues } = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-800 pt-4">Nuevo Usuario Hosting</h2>',
      html: `
        <div class="flex flex-col gap-4 p-4 text-left">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Nombre Completo</label>
            <input id="swal-nombre" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="Nombre del cliente">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email / Usuario</label>
            <input id="swal-email" type="email" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="email@ejemplo.com">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Contraseña Panel</label>
            <input id="swal-pass" type="password" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="••••••••">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Crear Usuario',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#f3f4f6',
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'px-6 py-2.5 rounded-xl font-bold text-sm',
        cancelButton: 'px-6 py-2.5 rounded-xl font-bold text-sm text-gray-500'
      },
      buttonsStyling: false,
      preConfirm: () => {
        const nombre = (document.getElementById('swal-nombre') as HTMLInputElement).value
        const email = (document.getElementById('swal-email') as HTMLInputElement).value
        const password = (document.getElementById('swal-pass') as HTMLInputElement).value
        if (!nombre || !email || !password) {
          Swal.showValidationMessage('Todos los campos son obligatorios')
          return false
        }
        return { nombre, email, password }
      }
    })

    if (formValues) {
      const res = await fetch('/api/hosting/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      })
      if (res.ok) {
        Swal.fire('Éxito', 'Usuario creado correctamente', 'success')
        fetchHostingUsers()
      }
    }
  }

  const handleCreateAccount = async () => {
    const { value: formValues } = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-800 pt-4">Nueva Cuenta de Hosting</h2>',
      html: `
        <div class="flex flex-col gap-4 p-4 text-left">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-blue-600 uppercase tracking-wider ml-1">1. Seleccionar Usuario Hosting</label>
            <div class="relative">
              <input id="swal-user-search" list="hosting-users-list" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-gray-50 shadow-sm text-sm" placeholder="Escribe para buscar cliente...">
              <datalist id="hosting-users-list">
                ${hostingUsers.map(u => `<option value="${u.nombre} (${u.email})" data-id="${u.id}"></option>`).join('')}
              </datalist>
            </div>
          </div>
          <div class="flex flex-col gap-3 border-t pt-4">
            <div class="flex items-center justify-between">
              <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1 text-blue-600">2. Detalles del Sitio</label>
              <button id="btn-auto-gen" class="text-[10px] font-bold bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-200 transition-colors uppercase tracking-tight">
                ✨ Auto-generar Datos
              </button>
            </div>
            <div class="grid grid-cols-1 gap-3">
              <input id="swal-dominio" class="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm shadow-sm focus:border-blue-500 outline-none" placeholder="Dominio (ej: rockmamotors.com)">
              <input id="swal-folder" class="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm shadow-sm font-mono focus:border-blue-500 outline-none" placeholder="Carpeta (ej: rockma_web)">
              
              <div class="bg-gray-50 p-3 rounded-xl border border-gray-100 space-y-3">
                <p class="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center">Configuración de Base de Datos</p>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input id="swal-db" class="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-xs shadow-sm font-mono focus:border-blue-500 outline-none" placeholder="DB Name">
                  <input id="swal-dbuser" class="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-xs shadow-sm font-mono focus:border-blue-500 outline-none" placeholder="DB User">
                </div>
                <input id="swal-dbpass" class="w-full px-4 py-2.5 rounded-lg border border-gray-200 text-xs shadow-sm font-mono focus:border-blue-500 outline-none" placeholder="DB Password Principal">
              </div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Activar Hosting Now',
      confirmButtonColor: '#2563eb',
      customClass: { popup: 'rounded-2xl', confirmButton: 'px-6 py-3 rounded-xl font-bold text-sm w-full' },
      buttonsStyling: false,
      didOpen: () => {
        const btnGen = document.getElementById('btn-auto-gen')
        btnGen?.addEventListener('click', () => {
          const domInput = document.getElementById('swal-dominio') as HTMLInputElement
          const folderInput = document.getElementById('swal-folder') as HTMLInputElement
          const dbInput = document.getElementById('swal-db') as HTMLInputElement
          const dbUserInput = document.getElementById('swal-dbuser') as HTMLInputElement
          const dbPassInput = document.getElementById('swal-dbpass') as HTMLInputElement

          const domain = domInput.value.trim()
          if (!domain) {
            Swal.showValidationMessage('Escribe primero el dominio para auto-generar')
            return
          }

          // Limpiar dominio para generar nombres consistentes
          const cleanName = domain
            .replace(/^https?:\/\//, '')
            .split('.')[0]
            .toLowerCase()
            .replace(/[^a-z0-9]/g, '')

          // Generar Carpeta
          if (!folderInput.value) folderInput.value = cleanName

          // Generar DB Name y User (con prefijos)
          dbInput.value = `clie_${cleanName}`
          dbUserInput.value = cleanName // MariaDB/MySQL user usualmente es igual al nombre corto

          // Generar Password Robusta
          const chars = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*"
          let pass = ""
          for (let i = 0; i < 14; i++) pass += chars.charAt(Math.floor(Math.random() * chars.length))
          dbPassInput.value = pass
        })
      },
      preConfirm: () => {
        const userSearch = (document.getElementById('swal-user-search') as HTMLInputElement).value
        const selectedUser = hostingUsers.find(u => `${u.nombre} (${u.email})` === userSearch)
        const d = (document.getElementById('swal-dominio') as HTMLInputElement).value
        const f = (document.getElementById('swal-folder') as HTMLInputElement).value
        const db = (document.getElementById('swal-db') as HTMLInputElement).value
        const dbU = (document.getElementById('swal-dbuser') as HTMLInputElement).value
        const dbP = (document.getElementById('swal-dbpass') as HTMLInputElement).value

        if (!selectedUser) {
          Swal.showValidationMessage('Debes seleccionar un usuario válido')
          return false
        }
        if (!d || !f) {
          Swal.showValidationMessage('Dominio y Carpeta obligatorios')
          return false
        }

        return { userId: selectedUser.id, dominio: d, folderName: f, dbName: db, dbUser: dbU, dbPass: dbP }
      }
    })

    if (formValues) {
      const res = await fetch('/api/hosting/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      })
      if (res.ok) {
        Swal.fire('Activado', 'Cuenta activada y base de datos configurada', 'success')
        fetchAccounts()
      }
    }
  }

  const handleEditUser = async (user: any) => {
    const { value: formValues } = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-800 pt-4">Editar Usuario Hosting</h2>',
      html: `
        <div class="flex flex-col gap-4 p-4 text-left">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Nombre Completo</label>
            <input id="swal-nombre" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="Nombre del cliente" value="${user.nombre}">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Email / Usuario</label>
            <input id="swal-email" type="email" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="email@ejemplo.com" value="${user.email}">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Nueva Contraseña (opcional)</label>
            <input id="swal-pass" type="password" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="Dejar en blanco para no cambiar">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar Cambios',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#2563eb',
      buttonsStyling: false,
      customClass: {
        popup: 'rounded-2xl',
        confirmButton: 'px-6 py-2.5 rounded-xl font-bold text-sm',
        cancelButton: 'px-6 py-2.5 rounded-xl font-bold text-sm text-gray-500'
      },
      preConfirm: () => {
        const nombre = (document.getElementById('swal-nombre') as HTMLInputElement).value
        const email = (document.getElementById('swal-email') as HTMLInputElement).value
        const password = (document.getElementById('swal-pass') as HTMLInputElement).value
        if (!nombre || !email) {
          Swal.showValidationMessage('Nombre y Email son obligatorios')
          return false
        }
        return { id: user.id, nombre, email, password: password || undefined }
      }
    })

    if (formValues) {
      const res = await fetch('/api/hosting/users', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      })
      if (res.ok) {
        Swal.fire('Guardado', 'Usuario actualizado correctamente', 'success')
        fetchHostingUsers()
      }
    }
  }

  const handleEditAccount = async (acc: any) => {
    const { value: formValues } = await Swal.fire({
      title: '<h2 class="text-2xl font-bold text-gray-800 pt-4">Editar Cuenta de Hosting</h2>',
      html: `
        <div class="flex flex-col gap-4 p-4 text-left">
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Dominio</label>
            <input id="swal-dominio" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm" placeholder="ej: mi-tienda.com" value="${acc.dominio}">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Carpeta de Archivos</label>
            <input id="swal-folder" class="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white shadow-sm text-sm font-mono" placeholder="ej: mi_tienda" value="${acc.folderName}">
          </div>
          <div class="flex flex-col gap-1.5">
            <label class="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">Base de Datos</label>
            <div class="grid grid-cols-1 gap-2">
              <input id="swal-db" class="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm font-mono" placeholder="DB Name" value="${acc.dbName || ''}">
              <input id="swal-dbuser" class="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm font-mono" placeholder="DB User" value="${acc.dbUser || ''}">
              <input id="swal-dbpass" class="w-full px-4 py-2 rounded-lg border border-gray-200 text-sm font-mono" placeholder="DB Pass" value="${acc.dbPass || ''}">
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Guardar Cambios',
      confirmButtonColor: '#2563eb',
      buttonsStyling: false,
      customClass: { popup: 'rounded-2xl', confirmButton: 'px-6 py-2.5 rounded-xl font-bold text-sm' },
      preConfirm: () => {
        const dominio = (document.getElementById('swal-dominio') as HTMLInputElement).value
        const folderName = (document.getElementById('swal-folder') as HTMLInputElement).value
        const dbName = (document.getElementById('swal-db') as HTMLInputElement).value
        const dbUser = (document.getElementById('swal-dbuser') as HTMLInputElement).value
        const dbPass = (document.getElementById('swal-dbpass') as HTMLInputElement).value

        if (!dominio || !folderName) {
          Swal.showValidationMessage('Dominio y Carpeta son obligatorios')
          return false
        }
        return { id: acc.id, dominio, folderName, dbName, dbUser, dbPass }
      }
    })

    if (formValues) {
      const res = await fetch('/api/hosting/accounts', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formValues)
      })
      if (res.ok) {
        Swal.fire('Guardado', 'Cuenta actualizada correctamente', 'success')
        fetchAccounts()
      }
    }
  }

  const handleDeleteAccount = async (id: number) => {
    const result = await Swal.fire({ title: '¿Eliminar cuenta?', icon: 'warning', showCancelButton: true })
    if (result.isConfirmed) {
      await fetch(`/api/hosting/accounts?id=${id}`, { method: 'DELETE' })
      fetchAccounts()
    }
  }

  const handleDeleteUser = async (id: number) => {
    const result = await Swal.fire({ title: '¿Eliminar usuario?', text: 'Se borrarán todas sus cuentas asociadas', icon: 'warning', showCancelButton: true })
    if (result.isConfirmed) {
      await fetch(`/api/hosting/users?id=${id}`, { method: 'DELETE' })
      fetchData()
    }
  }

  if (isAdmin === null) return null
  if (!isAdmin) return <HostingOverviewPage />

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Hosting (WHM)</h1>
          <p className="text-gray-500 text-sm">Panel de administración para servicios de alojamiento.</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'users' ? (
            <Button onClick={handleCreateUser} className="gap-2 bg-blue-600 shadow-lg shadow-blue-200">
              <FiPlus /> Nuevo Usuario Hosting
            </Button>
          ) : (
            <Button onClick={handleCreateAccount} className="gap-2 bg-blue-600 shadow-lg shadow-blue-200">
              <FiPlus /> Nueva Cuenta
            </Button>
          )}
        </div>
      </div>

      <div className="flex border-b border-gray-200 mb-6">
        <button 
          onClick={() => setActiveTab('users')}
          className={`px-6 py-3 text-sm font-bold transition-all border-b-2 ${activeTab === 'users' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <FiUsers className="inline mr-2" /> Usuarios Hosting ({hostingUsers.length})
        </button>
        <button 
          onClick={() => setActiveTab('accounts')}
          className={`px-6 py-3 text-sm font-bold transition-all border-b-2 ${activeTab === 'accounts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}
        >
          <FiServer className="inline mr-2" /> Cuentas Activas ({accounts.length})
        </button>
      </div>

      <Card className="border-none shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {activeTab === 'accounts' ? (
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Dominio</TableHead>
                  <TableHead>Carpeta</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {accounts.map((acc) => (
                  <TableRow key={acc.id} className="hover:bg-gray-50/50">
                    <TableCell>
                      <div className="font-bold text-gray-900">{acc.user?.nombreCompleto || acc.user?.nombre}</div>
                      <div className="text-xs text-gray-400">{acc.user?.email}</div>
                    </TableCell>
                    <TableCell className="font-medium text-blue-600 max-w-[200px] truncate" title={acc.dominio}>{acc.dominio}</TableCell>
                    <TableCell className="font-mono text-xs">{acc.folderName || 'No asignada'}</TableCell>
                    <TableCell className="text-right flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditAccount(acc)} className="text-gray-300 hover:text-blue-600"><FiCode /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteAccount(acc.id)} className="text-gray-300 hover:text-red-600"><FiTrash2 /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader className="bg-gray-50/50">
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Fecha Registro</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {hostingUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-gray-50/50">
                    <TableCell className="font-bold text-gray-900">{user.nombre}</TableCell>
                    <TableCell className="text-gray-600">{user.email}</TableCell>
                    <TableCell className="text-xs text-gray-400">{new Date(user.fechaRegistro).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleEditUser(user)} className="text-gray-300 hover:text-blue-600"><FiCode /></Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDeleteUser(user.id)} className="text-gray-300 hover:text-red-600"><FiTrash2 /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
